import { HELD_LINE_CAP_MS } from 'lib/twitchChat'
import type { ChatPlatform } from 'lib/twitchChatParse'

export const GATE_CAP_MS = HELD_LINE_CAP_MS
export const WAIT_NOTICE_MS = 120000
export const COMPLETE_PARTS_WINDOW_MS = 10000
export const REMOVED_IDS_MAX = 2000
export const FP_INDEX_MAX = 4000
export const BANNED_AUTHOR_MS = 300000
export const BANNED_AUTHORS_MAX = 2000
export const RECHECK_GATE = 'RECHECK'

export const FOREIGN_INTERIM_COPY = false

const FP_SHAPE = /^[0-9a-f]{16}$/

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const isPositiveInteger = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0

export type EnvelopeGate =
  | { kind: 'legacy' }
  | { kind: 'gated', gate: string, sentAt: number, sessionId: string }
  | { kind: 'malformed' }

export const envelopeGateOf = (envelope: object): EnvelopeGate => {
  if (!Object.prototype.hasOwnProperty.call(envelope, 'gate')) return { kind: 'legacy' }
  const e = envelope as { gate?: unknown, sentAt?: unknown, sessionId?: unknown }
  if (typeof e.gate !== 'string' || e.gate === '') return { kind: 'malformed' }
  if (!isFiniteNumber(e.sentAt)) return { kind: 'malformed' }
  return { kind: 'gated', gate: e.gate, sentAt: e.sentAt, sessionId: typeof e.sessionId === 'string' ? e.sessionId : '' }
}

export const rowFpOf = (raw: unknown, gate: string): string | null | 'malformed' => {
  const fp = (raw as { fp?: unknown } | null)?.fp
  if (typeof fp === 'string' && FP_SHAPE.test(fp)) return fp
  return gate === RECHECK_GATE ? 'malformed' : null
}

export interface YouTubeGateFields {
  ytGate?: string | null
  ytEnvelopeGate?: string
  ytSentAt?: number
  ytFp?: string | null
  ytSessionId?: string
}

export const arrivalGateOf = (envelopeGate: string, delayMs: number): string | null =>
  delayMs > 0 ? envelopeGate : null

export const gateOnDelayRise = <E extends YouTubeGateFields & { gated?: boolean }>(entries: E[], delayMs: number): E[] => {
  if (delayMs <= 0) return entries
  if (!entries.some((e) => e.ytEnvelopeGate != null && e.ytGate == null)) return entries
  return entries.map((e) => (e.ytEnvelopeGate != null && e.ytGate == null ? { ...e, ytGate: e.ytEnvelopeGate, gated: true } : e))
}

export const waitingLongCount = (entries: ReadonlyArray<YouTubeGateFields & { message: { receivedAt: number } }>, now: number): number =>
  entries.filter((e) => e.ytGate != null && now - e.message.receivedAt > WAIT_NOTICE_MS).length

export type GateVerdict = 'release' | 'wait' | 'drop'

export interface GatedRowMeta {
  sentAt: number
  fp: string | null
  sessionId: string
}

export interface VerifiedEnvelope {
  kind?: unknown
  channelId?: unknown
  sessionId?: unknown
  verifiedAt?: unknown
  coversBefore?: unknown
  fps?: unknown
  part?: unknown
  parts?: unknown
  complete?: unknown
  fromSentAt?: unknown
  throughSentAt?: unknown
}

export interface VerifiedResult {
  purge: string[]
  fps: number
  matched: number
}

export interface YouTubeVerifyGateCounts {
  indexedFps: number
  matched: number
  inferredGone: number
  malformedWatermarks: number
}

export interface YouTubeVerifyGate {
  noteRow: (id: string, meta: GatedRowMeta) => void
  rowCount: () => number
  noteVerified: (envelope: VerifiedEnvelope, accepts: (channelId: string | undefined) => boolean, now: number) => VerifiedResult
  coversBeforeOf: (fp: string) => number | null
  verdict: (
    e: YouTubeGateFields & { message: { id: string, login: string, publishedAt: number, receivedAt: number } },
    delayMs: number,
    now: number,
    serverReady: boolean
  ) => GateVerdict
  noteRemoved: (platform: ChatPlatform, ids: readonly string[]) => void
  isRemoved: (platform: ChatPlatform, id: string) => boolean
  noteBan: (envelope: { authorChannelId?: unknown, bannedAtMs?: unknown }, now: number) => void
  isBanned: (authorChannelId: string, publishedAt: number, now: number) => boolean
  youtubeRowGone: (m: { id: string, login: string, publishedAt: number }, now: number) => boolean
  counts: () => YouTubeVerifyGateCounts
  sizes: () => { fpIndex: number, removed: number, banned: number, rows: number, assemblies: number }
}

const setFresh = <K, V>(m: Map<K, V>, k: K, v: V, max: number): void => {
  m.delete(k)
  m.set(k, v)
  while (m.size > max) {
    const oldest = m.keys().next()
    if (oldest.done === true) break
    m.delete(oldest.value)
  }
}

interface Assembly {
  firstAt: number
  parts: number
  fromSentAt: number
  throughSentAt: number
  got: Map<number, readonly string[]>
  dead: boolean
}

const removedKey = (platform: ChatPlatform, id: string): string => `${platform}\n${id}`

export const createYouTubeVerifyGate = ({ rowMemoryMax }: { rowMemoryMax: number }): YouTubeVerifyGate => {
  const fpIndex = new Map<string, number>()
  const removed = new Map<string, true>()
  const banned = new Map<string, { at: number, until: number }>()
  const rows = new Map<string, GatedRowMeta>()
  const assemblies = new Map<string, Assembly>()
  const c: YouTubeVerifyGateCounts = { indexedFps: 0, matched: 0, inferredGone: 0, malformedWatermarks: 0 }

  const noteRemoved = (platform: ChatPlatform, ids: readonly string[]): void => {
    ids.forEach((id) => {
      if (typeof id === 'string' && id !== '') setFresh(removed, removedKey(platform, id), true, REMOVED_IDS_MAX)
    })
  }
  const isRemoved = (platform: ChatPlatform, id: string): boolean => removed.has(removedKey(platform, id))

  const isBanned = (authorChannelId: string, publishedAt: number, now: number): boolean => {
    const b = banned.get(authorChannelId)
    if (b == null) return false
    if (now >= b.until) {
      banned.delete(authorChannelId)
      return false
    }
    return !(publishedAt > b.at)
  }

  const youtubeRowGone = (m: { id: string, login: string, publishedAt: number }, now: number): boolean =>
    isRemoved('YOUTUBE', m.id) || (m.login !== '' && isBanned(m.login, m.publishedAt, now))

  const pruneAssemblies = (now: number): void => {
    Array.from(assemblies.entries()).forEach(([k, a]) => {
      if (now - a.firstAt > COMPLETE_PARTS_WINDOW_MS) assemblies.delete(k)
    })
  }

  const rowsOfSession = (sessionId: string): number =>
    Array.from(rows.values()).filter((r) => r.sessionId === sessionId).length

  const noteVerified = (envelope: VerifiedEnvelope, accepts: (channelId: string | undefined) => boolean, now: number): VerifiedResult => {
    const none: VerifiedResult = { purge: [], fps: 0, matched: 0 }
    const e = envelope
    const valid = typeof e.sessionId === 'string' && e.sessionId !== '' &&
      isFiniteNumber(e.verifiedAt) && isFiniteNumber(e.coversBefore) &&
      Array.isArray(e.fps) && isPositiveInteger(e.part) && isPositiveInteger(e.parts) && e.part <= e.parts &&
      e.complete === 1 && isFiniteNumber(e.fromSentAt) && isFiniteNumber(e.throughSentAt)
    if (!valid) {
      c.malformedWatermarks += 1
      return none
    }
    const sessionId = e.sessionId as string
    const verifiedAt = e.verifiedAt as number
    const coversBefore = e.coversBefore as number
    const part = e.part as number
    const parts = e.parts as number
    const fromSentAt = e.fromSentAt as number
    const throughSentAt = e.throughSentAt as number
    const fps = (e.fps as unknown[]).filter((fp): fp is string => typeof fp === 'string' && FP_SHAPE.test(fp))
    const channelId = typeof e.channelId === 'string' && e.channelId !== '' ? e.channelId : undefined
    const accepted = accepts(channelId)
    const heldFps = new Set(Array.from(rows.values()).map((r) => r.fp).filter((fp): fp is string => fp != null))
    let matched = 0
    if (accepted) {
      fps.forEach((fp) => {
        const prev = fpIndex.get(fp)
        setFresh(fpIndex, fp, prev == null ? coversBefore : Math.max(prev, coversBefore), FP_INDEX_MAX)
        if (heldFps.has(fp)) matched += 1
      })
      c.indexedFps += fps.length
      c.matched += matched
    }
    pruneAssemblies(now)
    if (!accepted && rowsOfSession(sessionId) === 0) return { purge: [], fps: fps.length, matched }
    const key = `${sessionId}\n${String(verifiedAt)}`
    let a = assemblies.get(key)
    if (a == null) {
      a = { firstAt: now, parts, fromSentAt, throughSentAt, got: new Map(), dead: false }
      assemblies.set(key, a)
    }
    if (a.dead) return { purge: [], fps: fps.length, matched }
    if (a.parts !== parts || a.fromSentAt !== fromSentAt || a.throughSentAt !== throughSentAt) {
      a.dead = true
      c.malformedWatermarks += 1
      return { purge: [], fps: fps.length, matched }
    }
    a.got.set(part, fps)
    if (a.got.size < a.parts) return { purge: [], fps: fps.length, matched }
    a.dead = true
    const listed = new Set<string>()
    a.got.forEach((list) => list.forEach((fp) => listed.add(fp)))
    const purge = Array.from(rows.entries())
      .filter(([, r]) => r.sessionId === sessionId && fromSentAt < r.sentAt && r.sentAt <= throughSentAt && (r.fp == null || !listed.has(r.fp)))
      .map(([id]) => id)
    purge.forEach((id) => rows.delete(id))
    noteRemoved('YOUTUBE', purge)
    c.inferredGone += purge.length
    return { purge, fps: fps.length, matched }
  }

  return {
    noteRow: (id, meta) => {
      if (id === '' || rows.has(id)) return
      setFresh(rows, id, { sentAt: meta.sentAt, fp: meta.fp, sessionId: meta.sessionId }, rowMemoryMax)
    },
    rowCount: () => rows.size,
    noteVerified,
    coversBeforeOf: (fp) => fpIndex.get(fp) ?? null,
    verdict: (e, delayMs, now, serverReady) => {
      if (now - e.message.receivedAt >= GATE_CAP_MS) return 'drop'
      if (e.ytGate !== RECHECK_GATE) return 'wait'
      if (!serverReady) return 'wait'
      if (youtubeRowGone(e.message, now)) return 'wait'
      if (e.ytFp == null || e.ytSentAt == null) return 'wait'
      const covers = fpIndex.get(e.ytFp)
      if (covers == null || covers < e.ytSentAt + Math.max(0, delayMs)) return 'wait'
      return 'release'
    },
    noteRemoved,
    isRemoved,
    noteBan: (envelope, now) => {
      const author = envelope.authorChannelId
      const at = envelope.bannedAtMs
      if (typeof author !== 'string' || author === '') return
      if (!isFiniteNumber(at) || at <= 0) return
      const prev = banned.get(author)
      setFresh(banned, author, { at: prev == null ? at : Math.max(prev.at, at), until: now + BANNED_AUTHOR_MS }, BANNED_AUTHORS_MAX)
    },
    isBanned,
    youtubeRowGone,
    counts: () => ({ ...c }),
    sizes: () => ({ fpIndex: fpIndex.size, removed: removed.size, banned: banned.size, rows: rows.size, assemblies: assemblies.size })
  }
}
